import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Box, IconButton } from "@mui/material";
import { FaTimes } from "react-icons/fa";
import BtnLoader from "Components/Common/Loader/BtnLoader";

const style = {
  position: "absolute",
  maxHeight: "84%",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 600,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
  overflow: "auto",
};

const CoinPromoEdit = ({
  editPromoModal,
  handleEditModalClose,
  PromoDetails,
  fetchPromos,
  staffId,
}) => {
  const [formData, setFormData] = useState({
    code: "",
    discount: "",
    usageLimit: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (PromoDetails) {
      setFormData({
        code: PromoDetails.code || "",
        discount: PromoDetails.discount || "",
        usageLimit: PromoDetails.usageLimit || "",
      });
    }
  }, [PromoDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const createStaffActivityLog = async (actionType, description) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
        {
          staff: staffId,
          action: actionType,
          description,
        }
      );
    } catch (error) {
      console.error("Activity log failed:", error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    // Validate input
    if (!formData.code || !formData.discount || !formData.usageLimit) {
      setError("All fields are required.");
      setIsLoading(false);
      return;
    }

    const data = JSON.stringify({
      code: formData.code,
      discount: formData.discount,
      usageLimit: formData.usageLimit,
    });

    const config = {
      method: "put",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/coin-plan-promo/updatepromo-plans/${PromoDetails?._id}`,
      headers: {
        "Content-Type": "application/json",
      },
      data,
    };

    try {
      const response = await axios.request(config);

      //  Activity Log Integration
      await createStaffActivityLog(
        "UPDATE_COIN_PROMO",
        `Promo code "${formData.code}" updated (Discount: ${formData.discount}%, Usage Limit: ${formData.usageLimit})`
      );

      setSuccess("Promo code updated successfully!");
      fetchPromos();
      handleEditModalClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update promo code.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={editPromoModal}
      onClose={handleEditModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <IconButton
          aria-label="close"
          onClick={handleEditModalClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#c51369",
          }}
        >
          <FaTimes />
        </IconButton>
        <div className="mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="mb-6 text-center text-2xl font-bold text-navy-700">
              Edit Promo Code
            </h2>

            {error && <p className="mb-4 text-center text-red-500">{error}</p>}
            {success && (
              <p className="mb-4 text-center text-green-500">{success}</p>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="code"
                  className="mb-2 block text-lg tracking-wide"
                >
                  Promo Code
                </label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  className="w-full rounded-md border p-2"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="discount"
                  className="mb-2 block text-lg tracking-wide"
                >
                  Discount (%)
                </label>
                <input
                  type="number"
                  id="discount"
                  name="discount"
                  value={formData.discount}
                  onChange={handleChange}
                  className="w-full rounded-md border p-2"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="usageLimit"
                  className="mb-2 block text-lg tracking-wide"
                >
                  Usage Limit
                </label>
                <input
                  type="number"
                  id="usageLimit"
                  name="usageLimit"
                  value={formData.usageLimit}
                  onChange={handleChange}
                  className="w-full rounded-md border p-2"
                  required
                />
              </div>
            </div>

            <div className="mt-6">
              {isLoading ? (
                <button
                  className="flex w-full items-center justify-center rounded-md bg-[#c51369] p-3 font-medium text-white"
                  disabled
                >
                  <BtnLoader />
                </button>
              ) : (
                <button
                  type="submit"
                  className="w-full rounded-md bg-[#c51369] p-3 font-medium text-white transition duration-200 hover:bg-[#a10f5b]"
                >
                  Save Changes
                </button>
              )}
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default CoinPromoEdit;
